import { createContext, useContext, useEffect, useState } from "react";

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", label: "Malayalam", native: "മലയാളം" },
  { code: "nl", label: "Dutch", native: "Nederlands" },
];

// Translations for the public site's most visible strings. Falls back to English
// for any missing key so the app never shows blanks.
const STRINGS = {
  "nav.home": { en: "Home", hi: "होम", te: "హోమ్", ta: "முகப்பு", kn: "ಮುಖಪುಟ", ml: "ഹോം" },
  "nav.services": { en: "Services", hi: "सेवाएं", te: "సేవలు", ta: "சேவைகள்", kn: "ಸೇವೆಗಳು", ml: "സേവനങ്ങൾ" },
  "nav.journal": { en: "Journal", hi: "जर्नल", te: "జర్నల్", ta: "இதழ்", kn: "ಜರ್ನಲ್", ml: "ജേണൽ" },
  "nav.second_opinion": { en: "Second opinion", hi: "दूसरी राय", te: "రెండవ అభిప్రాయం", ta: "இரண்டாம் கருத்து", kn: "ಎರಡನೇ ಅಭಿಪ್ರಾಯ", ml: "രണ്ടാം അഭിപ്രായം" },
  "nav.about": { en: "About", hi: "हमारे बारे में", te: "మా గురించి", ta: "எங்களைப் பற்றி", kn: "ನಮ್ಮ ಬಗ್ಗೆ", ml: "ഞങ്ങളെക്കുറിച്ച്" },
  "nav.patient_login": { en: "Patient login", hi: "रोगी लॉगिन", te: "రోగి లాగిన్", ta: "நோயாளர் உள்நுழைவு", kn: "ರೋಗಿ ಲಾಗಿನ್", ml: "രോഗി ലോഗിൻ" },
  "nav.book": { en: "Book Consultation", hi: "परामर्श बुक करें", te: "సంప్రదింపు బుక్ చేయండి", ta: "ஆலோசனையை முன்பதிவு செய்", kn: "ಸಮಾಲೋಚನೆ ಬುಕ್ ಮಾಡಿ", ml: "കൺസൾട്ടേഷൻ ബുക്ക് ചെയ്യുക" },
  "lang.label": { en: "Language", hi: "भाषा", te: "భాష", ta: "மொழி", kn: "ಭಾಷೆ", ml: "ഭാഷ" },
  "home.cta_book": { en: "Book a consultation", hi: "परामर्श बुक करें", te: "సంప్రదింపు బుక్ చేయండి", ta: "ஆலோசனையை முன்பதிவு செய்", kn: "ಸಮಾಲೋಚನೆ ಬುಕ್ ಮಾಡಿ", ml: "കൺസൾട്ടേഷൻ ബുക്ക് ചെയ്യുക" },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "en");
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const setLang = (code) => { setLangState(code); localStorage.setItem("lang", code); };
  const t = (key) => {
    const entry = STRINGS[key];
    if (!entry) return key;
    return entry[lang] || entry.en || key;
  };
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

// Translation applies only to the public website and the patient portal.
// Doctor, Admin and Customer-Support consoles are never translated.
export const isTranslatableRoute = (path) =>
  !/^\/(doctor|admin|support)(\/|$)/.test(path || "/");

